import React from 'react'
import { AppState } from '../../stores/app'
import { Container } from 'typescript-ioc/es5'
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons'
import { Select } from 'antd'
import { Input as AqeInput } from '@pushly/aqe/lib/components'
import { preventBubbling } from '../../_utils/utils'
import { axiosCancellationRequests } from '../../config/axios-setup'
import { SwPortal } from '../sw-portal/sw-portal'
import * as randomstring from 'randomstring'
import { PromptService } from '../../services/prompt'
import './prompt-search-bar.scss'
import {
    GetAbstractPromptResponse,
    isGetAbstractPromptResponse,
    isGetPromptGroupResponse,
} from '@pushly/models/lib/structs'
import { GetPromptGroupResponse } from '@pushly/models/lib/structs/prompts/get-prompt-group-response'
import { getEnabledDeliveryChannels } from '../../_utils/domain'
import { getDeliveryChannelLongName } from '@pushly/models/lib/enums/delivery-channels.enum'

export class PromptSearchBarAugmentedPrompt extends GetAbstractPromptResponse {
    public promptGroup!: GetPromptGroupResponse
}

type SearchEntity = GetPromptGroupResponse | PromptSearchBarAugmentedPrompt

type BaseSearchBarProps<T extends SearchEntity> = {
    className?: string
    loading?: boolean
    disabled?: boolean
    autofocus?: boolean
    displayStaticOptions?: boolean
    mode?: 'display' | 'search'
    entityLevel?: 'prompt' | 'promptGroup'
    placeholder?: string
    reactive?: boolean
    options?: T[]
    value?: T['id']
    onChange?: (value?: T['id']) => void
    renderOption?: (option: T) => React.ReactNode
    getDropdownContainer?: () => HTMLElement | any
}

type Props<T extends SearchEntity> = BaseSearchBarProps<T> &
    (T extends GetPromptGroupResponse ? { entityLevel: 'promptGroup' } : { entityLevel?: 'prompt' })

interface IPromptSearchBarState {
    expanded: boolean
    searching: boolean
    entities: SearchEntity[]
    tmpValue?: number
    searchQuery: string
}

export class PromptSearchBar<T extends SearchEntity> extends React.Component<Props<T>, IPromptSearchBarState> {
    private defaultClassName = 'sw-v2-prompt-search-bar'
    private axiosCancellationKey = 'psb.searchPrompts'

    private appState: AppState
    private promptService: PromptService

    private uid: string = randomstring.generate()
    private ref: any
    private inputRef: any
    private debounceTimer: any
    private debounceValue = 450

    public constructor(props: Props<T>) {
        super(props)

        this.appState = Container.get(AppState)
        this.promptService = Container.get(PromptService)

        this.state = {
            expanded: false,
            searching: false,
            entities: [],
            searchQuery: '',
        }
    }

    public async componentDidMount(): Promise<void> {
        this.wireEventHandlers()

        if (this.props.value) {
            return this.searchPrompts(this.props.value.toString(), true)
        } else if (this.props.autofocus && !!this.inputRef && !!this.inputRef.inputRef) {
            this.inputRef.inputRef.focus()
        }
    }

    public componentDidUpdate(prevProps: Props<T>): void {
        if (this.isDisplayMode && this.props.value) {
            if (!this.state.tmpValue || this.props.value!.toString() !== this.state.tmpValue.toString()) {
                this.searchPrompts(this.props.value.toString(), true)
            }
        }

        if (this.props.mode !== prevProps.mode && this.props.mode === 'search' && !!this.props.value && !!this.selectedPrompt) {
            this.setState({
                searchQuery: `#${this.selectedPrompt.id}: ${this.parseNameValue(this.selectedPrompt)}`
            }, () => {
                if (!!this.inputRef && !!this.inputRef.input) {
                    this.inputRef.input.focus()
                    this.inputRef.input.select()
                }
            })
        }
    }

    public componentWillUnmount() {
        this.unwireEventHandlers()
    }

    public render(): React.ReactNode {
        const showStaticOptions = this.props.displayStaticOptions && Array.isArray(this.props.options)
        const placeholder = this.props.placeholder || 'Search by ID or Title'

        return (
            <div ref={(el) => (this.ref = el)} className={this.buildRootClassNames()}>
                <div className={this.buildClassName('wrapper')}>
                    <span className={this.buildClassName('display')}>
                        {!!this.props.value ? (
                            <>
                                <span className="option-id">#{this.props.value}:</span>
                                &nbsp;
                                <span className="option-title">
                                    {this.selectedPrompt ? (
                                        this.parseNameValue(this.selectedPrompt)
                                    ) : (
                                        <LoadingOutlined spin={true} />
                                    )}
                                </span>
                            </>
                        ) : (
                            '...'
                        )}
                    </span>

                    <div className={this.buildClassName('search')}>
                        {showStaticOptions ? (
                            <Select
                                popupClassName={this.buildRootClassNames('dropdown')}
                                placeholder={this.props.loading ? <LoadingOutlined spin={true} /> : placeholder}
                                value={this.props.value}
                                onChange={this.handleOptionSelect}
                                disabled={this.props.loading}
                            >
                                {this.props.options!.map((option: SearchEntity) => {
                                    return (
                                        <Select.Option key={option.id} value={option.id}>
                                            {!!this.props.renderOption
                                                ? this.props.renderOption(option as T)
                                                : this.renderOption(option)}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        ) : (
                            <AqeInput
                                ref={(el) => (this.inputRef = el)}
                                className={this.buildClassName('search-input')}
                                reactive={this.props.reactive !== false}
                                value={this.state.searchQuery}
                                onChange={this.handleInputChange}
                                onFocus={(ev) => {
                                    ev.target.select()
                                    this.activate()
                                }}
                                placeholder={placeholder}
                                maxWidth={400}
                                icon={
                                    this.state.searching ? (
                                        <LoadingOutlined className="search-icon" spin={this.state.searching} />
                                    ) : (
                                        <SearchOutlined className="search-icon" spin={this.state.searching} />
                                    )
                                }
                            />
                        )}
                    </div>

                    <SwPortal
                        className={this.buildRootClassNames('portal')}
                        portalId={this.buildClassName(`portal-${this.uid}`)}
                        container={this.portalContainer}
                        style={this.getPortalPositioning()}
                    >
                        <div className={this.buildRootClassNames('dropdown')}>
                            <div className={this.buildClassName('dropdown-wrapper')}>
                                <div
                                    className={`${this.buildClassName('dropdown-options')}${
                                        !this.hasOptions ? ' empty' : ''
                                    }`}
                                >
                                    {!this.hasOptions ? (
                                        <span>{this.isSearching ? 'Searching ...' : 'No prompts found'}</span>
                                    ) : (
                                        this.state.entities.map((n) => this.renderOption(n))
                                    )}
                                </div>
                            </div>
                        </div>
                    </SwPortal>
                </div>
            </div>
        )
    }

    public renderOption(option: SearchEntity): React.ReactNode {
        const selected = this.props.value && this.props.value.toString() === option.id.toString()

        if (isGetAbstractPromptResponse(option)) {
            const groupIsAbTest = option.promptGroup.isAbTest || false
            const promptName = option.name || option.computedName || option.promptGroup.name || 'Unknown Prompt'

            return (
                <div
                    key={option.id}
                    className={`${this.buildClassName('dropdown-option')}${selected ? ' selected' : ''}`}
                >
                    <div
                        className={this.buildClassName('dropdown-option-wrapper')}
                        onClick={() => this.handleValueChange(option)}
                    >
                        {!!this.props.renderOption ? (
                            this.props.renderOption(option as T)
                        ) : (
                            <>
                                <div className={this.buildClassName('dropdown-option-upper')}>
                                    <span className="option-id">
                                        #{option.id} | {getDeliveryChannelLongName(option.promptGroup.channel)}
                                    </span>
                                    <span className="option-send-date">{groupIsAbTest && option.promptGroup.name}</span>
                                </div>
                                <div className={this.buildClassName('dropdown-option-lower')}>
                                    <span className="option-title">{promptName}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )
        }

        return (
            <div key={option.id} className={`${this.buildClassName('dropdown-option')}${selected ? ' selected' : ''}`}>
                <div
                    className={this.buildClassName('dropdown-option-wrapper')}
                    onClick={() => this.handleValueChange(option)}
                >
                    {!!this.props.renderOption ? (
                        this.props.renderOption(option as T)
                    ) : (
                        <>
                            <div className={this.buildClassName('dropdown-option-upper')}>
                                <span className="option-id">
                                    #{option.id} | {getDeliveryChannelLongName(option.channel)}
                                </span>
                                <span className="option-send-date"></span>
                            </div>
                            <div className={this.buildClassName('dropdown-option-lower')}>
                                <span className="option-title">{option.name}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        )
    }

    public getPortalPositioning(): any {
        if (this.ref) {
            const rect = this.ref.getBoundingClientRect()

            return [
                ['position', 'absolute'],
                ['top', `${Math.floor(rect.top) + Math.floor(rect.height)}px`],
                ['left', `${Math.floor(rect.left)}px`],
                ['width', `${Math.floor(rect.width)}px`],
            ]
                .map((p) => p.join(':'))
                .join(';')
        }
    }

    protected get isDisplayMode(): boolean {
        return this.props.mode === 'display'
    }

    protected get isExpanded(): boolean {
        return this.state.expanded
    }

    protected get isSearching(): boolean {
        return this.state.searching
    }

    protected get hasOptions(): boolean {
        return this.state.entities.length > 0 || (this.props.options ?? []).length > 0
    }

    protected get hasInput(): boolean {
        return !!this.inputRef && !!this.inputRef.input.value.trim()
    }

    protected get inputValue(): string {
        return this.hasInput ? this.inputRef.input.value.trim() : ''
    }

    protected get selectedPrompt(): SearchEntity | undefined {
        let option: SearchEntity | undefined

        if (this.hasOptions && this.props.value) {
            const options = this.props.options ?? this.state.entities ?? []
            option = options.find((n) => n.id.toString() === this.props.value!.toString())
        }

        return option
    }

    protected get portalContainer(): HTMLElement {
        return !this.props.getDropdownContainer ? document.body : this.props.getDropdownContainer()
    }

    protected parseNameValue(option: SearchEntity): string {
        return isGetPromptGroupResponse(option)
            ? option.name
            : option.name || option.computedName || option.promptGroup.name || 'Unknown Prompt'
    }

    protected handleValueChange = async (entity: SearchEntity): Promise<void> => {
        // clear loaded tmpValue
        await this.asyncSetState({
            tmpValue: undefined,
            searchQuery: `#${entity.id}: ${this.parseNameValue(entity)}`,
        })

        this.props.onChange?.(entity.id)
        this.deactivate()
    }

    protected handleInputChange = async (ev: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const value = ev.target.value

        await this.asyncSetState({ searchQuery: value })

        this.props.onChange?.(undefined)

        if (value.length >= 3) {
            this.searchPrompts(value)
        } else {
            this.deactivate()
        }
    }

    protected handleOptionSelect = async (value?: SearchEntity['id']): Promise<void> => {
        if (value === undefined || value === null) {
            this.props.onChange?.(undefined)
            return
        }

        const options: SearchEntity[] = (this.props.options ?? []) as SearchEntity[]
        const selected = options.find((option) => option.id.toString() === value.toString())

        if (selected) {
            await this.handleValueChange(selected)
            return
        }

        this.props.onChange?.(value as T['id'])
    }

    protected activate = (): void => {
        this.debounce(() => {
            if (this.hasInput && this.inputValue.length > 3) {
                this.searchPrompts(this.inputValue)
            }
        }, this.debounceValue / 2)
    }

    protected deactivate = (): void => {
        this.debounce(() => {
            this.cancelRequests()
            return this.setState(() => ({ expanded: false }))
        }, this.debounceValue / 2)
    }

    protected cancelRequests = (): void => {
        if (this.axiosCancellationKey in axiosCancellationRequests) {
            axiosCancellationRequests[this.axiosCancellationKey]()
        }

        this.setState(() => ({
            searching: false,
            entities: [],
        }))
    }

    protected async searchPrompts(query: string, reload: boolean = false): Promise<void> {
        await this.asyncSetState(() => ({
            expanded: !reload,
            searching: true,
            tmpValue: reload ? (query as any) : undefined,
        }))

        if (!this.appState.currentDomain) return
        const domainId = this.appState.currentDomain.id

        const selectedPrettyString = this.selectedPrompt
            ? `#${this.selectedPrompt.id}: ${this.parseNameValue(this.selectedPrompt)}`
            : ''

        // If the query matches exactly what we have selected, search by ID only
        let searchQuery = query
        if (query === selectedPrettyString && this.selectedPrompt) {
            searchQuery = this.selectedPrompt.id.toString()
        }

        let entities = this.props.options

        this.debounce(async () => {
            let remoteFetchCancelled = false

            // Only perform API search if options are not explicitly passed
            if (!entities) {
                const res = await this.promptService.fetchPromptGroupsByDomainId(domainId, {
                    query: {
                        search: searchQuery,
                        pagination: 0,
                        // Prompt searching should include all channels
                        channels: getEnabledDeliveryChannels(this.appState.currentDomain!).join(','),
                        is_ab_test: this.props.entityLevel === 'promptGroup',
                        include_deleted: 1,
                    },
                    cancellationKey: `${this.axiosCancellationKey}-${this.uid}`,
                })

                if (res.ok && res.data) {
                    const promptGroups = res.data
                    remoteFetchCancelled = res.cancelled ?? false

                    if (this.props.entityLevel === 'promptGroup') {
                        entities = promptGroups as Props<T>['options']
                    } else {
                        const promptEntities: PromptSearchBarAugmentedPrompt[] = []
                        for (const group of promptGroups) {
                            for (const prompt of group.prompts) {
                                const augmented = PromptSearchBarAugmentedPrompt.from(
                                    prompt as PromptSearchBarAugmentedPrompt,
                                )
                                augmented.promptGroup = group
                                promptEntities.push(augmented)
                            }
                        }
                        entities = promptEntities as Props<T>['options']
                    }
                }
            }

            await this.asyncSetState(({ expanded }) => ({
                entities: entities ?? [],
                searching: expanded && remoteFetchCancelled,
                searchQuery:
                    reload && !!this.selectedPrompt
                        ? `#${this.selectedPrompt.id}: ${this.parseNameValue(this.selectedPrompt)}`
                        : this.state.searchQuery,
            }))
        })
    }

    protected debounce(fn: Function, timeout?: number) {
        if (!!this.debounceTimer) clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(fn, !!timeout ? timeout : this.debounceValue)
    }

    protected buildClassName(className: string): string {
        return `${this.defaultClassName}-${className}`
    }

    protected buildRootClassNames(append?: string): string {
        const classNames: string[] = [
            !!append ? `${this.defaultClassName}-${append}` : this.defaultClassName,
            this.isDisplayMode ? 'mode-display' : 'mode-search',
            this.isExpanded ? 'expanded' : 'collapsed',
            this.isSearching ? 'searching' : 'static',
        ]
        if (this.props.className) classNames.push(this.props.className)

        return classNames.join(' ')
    }

    protected wireEventHandlers(): void {
        const showStaticOptions = this.props.displayStaticOptions && Array.isArray(this.props.options)
        if (!this.props.loading && !showStaticOptions) {
            document.addEventListener('mousedown', this.handleDocumentClick.bind(this))
            this.ref.addEventListener('mousedown', preventBubbling)
        }
    }

    protected unwireEventHandlers(): void {
        document.removeEventListener('mousedown', this.handleDocumentClick.bind(this))
        this.ref.removeEventListener('mousedown', preventBubbling)
    }

    protected handleDocumentClick(_event: MouseEvent): void {
        if (this.isExpanded) this.deactivate()
    }

    protected async asyncSetState<S extends IPromptSearchBarState, K extends keyof S>(
        value: ((prevState: Readonly<S>, props: Readonly<Props<T>>) => Pick<S, K> | S | null) | (Pick<S, K> | S | null),
    ): Promise<void> {
        await new Promise<void>((res) => {
            this.setState(value as S, () => res())
        })
    }
}
